/**
 * MonitoredResourcesModal: which Microsoft 365 resources Guardian watches for
 * a tenant, on the Carbon Modal (lg) with a selectable, grouped DataTable:
 * one group per category, each with a "Select all in {category}" checkbox
 * (checked when every member is selected, mixed when some are), a toolbar
 * search, and a sticky header so the column row stays put while the list
 * scrolls.
 *
 * Saving a changed selection first asks for confirmation (ConfirmDialog):
 * the baseline needs a refresh afterwards so removed categories leave it.
 * The confirm dialog is rendered inside the Modal's body, so headless UI
 * treats it as a nested dialog and a click in it never closes the modal.
 *
 * Selection is kept by resource name (the API's shape). The table works on
 * the rows the search shows, so a change merges back into the full
 * selection and never drops a hidden, selected resource.
 */
import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { Modal } from '@/components/carbon/Modal'
import { TableSearch } from '@/components/carbon/TableSearch'

interface MonitoredResourcesModalProps {
  isOpen: boolean
  onClose: () => void
  tenant: {
    tenantId: string
    name: string
    domain?: string
  }
  onSuccess?: () => void
}

interface ResourceRow {
  id: string
  category: string
  resource: string
}

const rowIdOf = (category: string, resource: string) => `${category}/${resource}`

export default function MonitoredResourcesModal({
  isOpen,
  onClose,
  tenant,
  onSuccess
}: MonitoredResourcesModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set())
  const [showWarningDialog, setShowWarningDialog] = useState(false)

  const queryClient = useQueryClient()

  // Fetch current tenant resources
  const {
    data: currentResources = [],
    isLoading: loadingCurrent
  } = useQuery({
    queryKey: ['tenantResources', tenant.tenantId],
    queryFn: () => api.getTenantMonitoredResources(tenant.tenantId),
    enabled: isOpen && !!tenant.tenantId,
  })

  // Fetch available resources
  const {
    data: availableResources = {},
    isLoading: loadingAvailable
  } = useQuery({
    queryKey: ['availableResources'],
    queryFn: api.getAvailableResources,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (resources: string[]) => api.updateTenantMonitoredResources(tenant.tenantId, resources),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantResources', tenant.tenantId] })
      toast.success('Monitored resources saved.')
      onSuccess?.()
      onClose()
    },
    onError: (error: any) => {
      console.error('Monitored resources did not save:', error)
      toast.error(error.response?.data?.message || 'Failed to update monitored resources')
    },
  })

  // Initialize selected resources when current resources load
  useEffect(() => {
    if (currentResources.length > 0) {
      setSelectedResources(new Set(currentResources))
    }
  }, [currentResources])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedResources(new Set())
      setShowWarningDialog(false)
    }
  }, [isOpen])

  const handleSave = () => {
    const hasChanges =
      selectedResources.size !== currentResources.length ||
      !currentResources.every(resource => selectedResources.has(resource))

    if (hasChanges) {
      setShowWarningDialog(true)
    } else {
      handleConfirmSave()
    }
  }

  const handleConfirmSave = () => {
    setShowWarningDialog(false)
    updateMutation.mutate(Array.from(selectedResources))
  }

  // Filter resources based on search
  const filteredCategories = useMemo(
    () =>
      Object.keys(availableResources).reduce((acc, category) => {
        const resources = availableResources[category] || []
        const filteredResources = resources.filter(resource =>
          resource.toLowerCase().includes(searchQuery.toLowerCase())
        )
        if (filteredResources.length > 0) {
          acc[category] = filteredResources
        }
        return acc
      }, {} as Record<string, string[]>),
    [availableResources, searchQuery],
  )

  const totalSelected = selectedResources.size
  const totalAvailable = Object.values(availableResources).flat().length
  const isLoading = loadingCurrent || loadingAvailable

  const rows = useMemo<ResourceRow[]>(
    () =>
      Object.entries(filteredCategories).flatMap(([category, resources]) =>
        resources.map(resource => ({ id: rowIdOf(category, resource), category, resource })),
      ),
    [filteredCategories],
  )

  // The table's selection is the shown rows that are selected, by row id.
  const selectedIds = rows.filter(r => selectedResources.has(r.resource)).map(r => r.id)

  // A change from the table covers the shown rows only (one row, a group, or the page select-all): set each shown
  // row from the table's selection and keep every hidden selection as it was.
  const handleSelectionChange = (ids: string[]) => {
    const picked = new Set(ids)
    const next = new Set(selectedResources)
    for (const row of rows) {
      if (picked.has(row.id)) next.add(row.resource)
      else next.delete(row.resource)
    }
    setSelectedResources(next)
  }

  const columns: DataColumn<ResourceRow>[] = [
    { id: 'resource', header: 'Resource', primary: true, cell: r => <span className="font-mono text-helper">{r.resource}</span> },
  ]

  const groupCount = (category: string) => {
    const all = availableResources[category] || []
    return `${all.filter(r => selectedResources.has(r)).length} of ${all.length} selected`
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="lg"
      title={`Monitored resources for ${tenant.name}`}
      subtitle="Choose which Microsoft 365 resources Guardian checks for configuration drift."
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: updateMutation.isPending }}
      primaryAction={{ label: 'Save changes', onClick: handleSave, disabled: isLoading, loading: updateMutation.isPending }}
    >
      <DataTable
        aria-label={`Monitored resources for ${tenant.name}`}
        description={isLoading ? undefined : `${totalSelected} of ${totalAvailable} resources selected.`}
        columns={columns}
        rows={rows}
        rowId={r => r.id}
        rowLabel={r => r.resource}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        groups={{
          key: r => r.category,
          label: category => category,
          defaultExpanded: true,
          header: category => (
            <span className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-4">
              <span>{category}</span>
              <span className="text-helper font-normal text-text-helper">{groupCount(category)}</span>
            </span>
          ),
        }}
        toolbar={<TableSearch value={searchQuery} onChange={setSearchQuery} label="Search resources" />}
        isLoading={isLoading}
        size="md"
        stickyHeader
        maxHeight="min(24rem, 50vh)"
        empty={searchQuery ? 'No resources match your search.' : 'Guardian has no resources to offer for this tenant.'}
      />

      {/* Rendered inside the Modal, so headless UI nests it: a click in it never closes the modal underneath. */}
      <ConfirmDialog
        open={showWarningDialog}
        title="Refresh the baseline after saving."
        body={
          <>
            <p className="m-0">
              You changed the monitored resources for <b className="font-semibold text-text-primary">{tenant.name}</b>.
            </p>
            <p className="m-0 mt-2">
              After saving, refresh the baseline so removed categories leave it. Ask your Guardian admin if you cannot run a refresh.
            </p>
          </>
        }
        confirmLabel="Save and continue"
        onConfirm={handleConfirmSave}
        onCancel={() => setShowWarningDialog(false)}
      />
    </Modal>
  )
}
